import { useState } from 'react';
import { useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon, IconButton } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import type { OrcamentoDeIa } from '@/data/tipos';
import { pode } from '@/session/sessao';

const numero = (v: number) => v.toLocaleString('pt-BR');

/**
 * Os tetos de consumo de IA, por clínica.
 *
 * A chave é da plataforma e a fatura chega para ela, mas quem gasta é a
 * clínica. Sem teto, uma clínica sozinha consome o orçamento de todas — e o
 * primeiro sinal disso seria a conta do provedor.
 *
 * **Sem teto é o padrão, e a tela diz isso em vez de mostrar zero.** Um limite
 * inventado pelo produto pararia a IA de quem nunca pediu limite nenhum.
 *
 * O teto vale no MÊS DO CALENDÁRIO. "Últimos 30 dias" faria a IA voltar
 * numa terça-feira qualquer, sem ninguém entender por quê.
 */
export function TetosDeIa({ orcamentos }: { orcamentos: OrcamentoDeIa[] }) {
    const { revalidate } = useRevalidator();
    const aviso = useAviso();
    const [editando, setEditando] = useState<OrcamentoDeIa | null>(null);
    const [valor, setValor] = useState('');
    const valorSujo = useAlteracaoPendente(valor, editando);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    const abrir = (o: OrcamentoDeIa) => {
        setEditando(o);
        setValor(o.limiteTokensMes === null ? '' : String(o.limiteTokensMes));
        setErro(null);
    };

    const salvar = async () => {
        if (!editando) return;
        const limpo = valor.trim();
        /* Campo vazio é "sem teto", e não zero. Zero seria um teto que nenhuma
           clínica alcança sem estar já estourada no primeiro uso. */
        const limite = limpo === '' ? null : Number(limpo);
        if (limite !== null && (!Number.isFinite(limite) || limite <= 0)) {
            setErro('O teto precisa ser maior que zero. Deixe em branco para não haver teto.');
            return;
        }
        setSalvando(true);
        setErro(null);
        try {
            await dados().salvarOrcamentoDeIa(editando.clinicaId, limite);
            aviso({
                title: limite === null ? 'Teto removido' : 'Teto definido',
                description: limite === null
                    ? `${editando.clinica} volta a consumir sem limite.`
                    : `${editando.clinica}: ${numero(limite)} tokens por mês.`,
            });
            setEditando(null);
            void revalidate();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar o teto.');
        } finally {
            setSalvando(false);
        }
    };

    const colunas: DataTableColumn<OrcamentoDeIa & { id: string }>[] = [
        { key: 'clinica', header: 'Clínica', render: (o) => <strong style={{ fontWeight: 600 }}>{o.clinica}</strong> },
        {
            key: 'tokensDoMes', header: 'Consumo do mês', align: 'right', width: 160,
            render: (o) => numero(o.tokensDoMes),
        },
        {
            key: 'limiteTokensMes', header: 'Teto', align: 'right', width: 150,
            render: (o) => (o.limiteTokensMes === null
                /* "Sem teto" escrito, e não um traço: traço é ambíguo entre "não
                   tem" e "não sei". */
                ? <span style={{ color: 'var(--text-tertiary)' }}>sem teto</span>
                : numero(o.limiteTokensMes)),
        },
        {
            key: 'estourado', header: 'Situação', width: 170,
            render: (o) => {
                if (o.limiteTokensMes === null) return <Badge tone="neutral">Sem limite</Badge>;
                if (o.estourado) return <Badge tone="danger">IA parada</Badge>;
                const pct = Math.round((o.tokensDoMes / o.limiteTokensMes) * 100);
                /* O aviso vem ANTES de acabar: descobrir o teto quando a
                   IA já parou é descobrir tarde. */
                return pct >= 80
                    ? <Badge tone="warning">{pct}% do teto</Badge>
                    : <Badge tone="success">{pct}% do teto</Badge>;
            },
        },
        {
            key: 'acao', header: '', align: 'right', width: 60,
            render: (o) => (pode('ia.tetos') ? (
                <IconButton
                    size="sm" icon={<Icon name="pencil" size={14} />}
                    label={`Definir teto de ${o.clinica}`}
                    onClick={() => abrir(o)}
                />
            ) : null),
        },
    ];

    const paradas = orcamentos.filter((o) => o.estourado);

    return (
        <>
            {paradas.length > 0 && (
                <Alert tone="warning" dismissible={false} title={paradas.length === 1 ? 'Uma clínica está sem IA' : `${String(paradas.length)} clínicas estão sem IA`}>
                    {paradas.map((o) => o.clinica).join(', ')} alcançou o teto do mês. A IA volta na
                    virada do mês, ou assim que o teto subir.
                </Alert>
            )}

            <Card
                title="Teto por clínica"
                subtitle={pode('ia.tetos')
                    ? 'Em tokens por mês do calendário. Em branco é sem teto — que é o padrão.'
                    : 'Em tokens por mês do calendário · mudar o teto pede a permissão “Definir o teto de consumo de IA por clínica”'}
                padding={0}
            >
                <DataTable
                    columns={colunas}
                    rows={orcamentos.map((o) => ({ ...o, id: o.clinicaId }))}
                    rowKey="id"
                />
            </Card>

            <Modal
                open={!!editando}
                onClose={() => setEditando(null)}
                dirty={valorSujo}
                title={editando ? `Teto de ${editando.clinica}` : 'Teto'}
                width={480}
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setEditando(null)}>Cancelar</Button>
                        <Button onClick={() => { void salvar(); }} loading={salvando}>Salvar</Button>
                    </>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                    <FormField
                        label="Tokens por mês"
                        hint="Entrada e saída somadas, que é como o provedor cobra. Em branco: sem teto."
                    >
                        <Input
                            type="number" value={valor} onChange={setValor}
                            placeholder="sem teto"
                            aria-label="Tokens por mês"
                        />
                    </FormField>

                    {editando && (
                        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                            Consumo deste mês: <strong>{numero(editando.tokensDoMes)}</strong> tokens.
                            {' '}Um teto abaixo disso para a IA desta clínica agora — e ela volta na virada do mês.
                        </p>
                    )}
                </div>
            </Modal>
        </>
    );
}
